import React, { useState } from 'react';
import { useMarketData } from '../context/MarketContext';
import { toEAT } from '../utils/time';
import { Panel } from './ui';

interface Props { onPriceSelect?: (price: number) => void; }

export default function OrderBookPanel({ onPriceSelect }: Props) {
  const { orderBook, lastUpdateTime, labelForSecId, selectedSymbol } = useMarketData();

  // L3 shows every resting order; L2 folds them into one row per price level, which is how
  // most venues present depth and what you want when the book is order-fragmented.
  const [depth, setDepth] = useState<'L2' | 'L3'>('L2');

  const aggregate = (side: typeof orderBook.bids) => {
    const byPrice = new Map<number, { price: number; size: number; orders: number }>();
    for (const lvl of side) {
      const cur = byPrice.get(lvl.price);
      if (cur) { cur.size += lvl.size; cur.orders += 1; }
      else byPrice.set(lvl.price, { price: lvl.price, size: lvl.size, orders: 1 });
    }
    return [...byPrice.values()];
  };

  // Aggregation must preserve the incoming sort: bids descend, asks ascend. A Map keeps
  // insertion order and the feed already arrives sorted, so no re-sort is needed -- but do not
  // rely on that if the feed ever changes.
  const topBids = depth === 'L2' ? aggregate(orderBook.bids) : orderBook.bids;
  const topAsks = depth === 'L2' ? aggregate(orderBook.asks) : orderBook.asks;
  const maxBidSize = topBids.reduce((m, b) => Math.max(m, b.size), 1);
  const maxAskSize = topAsks.reduce((m, a) => Math.max(m, a.size), 1);
  const overallMax = Math.max(maxBidSize, maxAskSize);

  // Detect if any entries have order IDs (L3 feed)
  const hasIds = orderBook.bids.some(b => !!b.id) || orderBook.asks.some(a => !!a.id);

  const spread = orderBook.bids[0] && orderBook.asks[0]
    ? (orderBook.asks[0].price - orderBook.bids[0].price).toFixed(2) : null;

  return (
    <Panel title="Order book" badge={labelForSecId(selectedSymbol)} flush>
      {/* Depth toggle + feed clock. The heading and symbol now live in the panel header. */}
      {(hasIds || lastUpdateTime) && (
        <div style={{ padding: '5px 12px', borderBottom: '1px solid #2a3540', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {hasIds ? (
            <span style={{ display: 'inline-flex', border: '1px solid #2a3540', borderRadius: 3, overflow: 'hidden' }}>
              {(['L2', 'L3'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setDepth(mode)}
                  title={mode === 'L2'
                    ? 'Aggregated: one row per price level'
                    : 'Market by order: every resting order shown separately'}
                  style={{
                    background: depth === mode ? '#4da3ff20' : 'transparent',
                    border: 'none', cursor: 'pointer', padding: '1px 6px',
                    fontSize: 8, fontWeight: 700, letterSpacing: '0.05em',
                    color: depth === mode ? '#4da3ff' : '#46525e',
                  }}
                >
                  {mode}
                </button>
              ))}
            </span>
          ) : <span />}
          {lastUpdateTime && <span style={{ fontSize: 10, color: '#2a3540' }}>{toEAT(lastUpdateTime)}</span>}
        </div>
      )}

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', borderBottom: '1px solid #10151b' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#36c98e' }}>PRICE</span>
          <span style={{ fontSize: 10, color: '#46525e' }}>SIZE</span>
        </div>
        <div style={{ backgroundColor: '#2a3540' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', borderBottom: '1px solid #10151b' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#ff5c5c' }}>PRICE</span>
          <span style={{ fontSize: 10, color: '#46525e' }}>SIZE</span>
        </div>
      </div>

      {/* Book rows */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr' }}>
        {/* Bids */}
        <div>
          {topBids.length > 0
            ? topBids.map((bid, i) => (
                <BookRow key={(bid as { id?: string }).id ?? `b${bid.price}`} p={bid.price} s={bid.size} orders={(bid as { orders?: number }).orders} maxSize={overallMax} side="bid" onPriceSelect={onPriceSelect} />
              ))
            : <Empty text="No bids" />}
        </div>
        <div style={{ backgroundColor: '#2a3540' }} />
        {/* Asks */}
        <div>
          {topAsks.length > 0
            ? topAsks.map((ask, i) => (
                <BookRow key={(ask as { id?: string }).id ?? `a${ask.price}`} p={ask.price} s={ask.size} orders={(ask as { orders?: number }).orders} maxSize={overallMax} side="ask" onPriceSelect={onPriceSelect} />
              ))
            : <Empty text="No asks" />}
        </div>
      </div>

      {/* Spread */}
      {spread && (
        <div style={{ borderTop: '1px solid #2a3540', padding: '5px 12px', textAlign: 'center', fontSize: 11, color: '#7a8693' }}>
          Spread <span style={{ fontWeight: 700, color: '#ff8c3a' }}>{spread}</span> TZS
        </div>
      )}
    </Panel>
  );
}

// P2-19: memoize so unchanged bid/ask rows skip re-render on every order-book tick.
const BookRow = React.memo(function BookRow({ p, s, orders, maxSize, side, onPriceSelect }: {
  p: number; s: number;
  /** How many resting orders make up this level. Only meaningful in the aggregated L2 view. */
  orders?: number;
  maxSize: number; side: 'bid' | 'ask';
  onPriceSelect?: (price: number) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const pct = Math.min(100, (s / maxSize) * 100);
  const isBid = side === 'bid';
  return (
    <div
      onClick={() => onPriceSelect?.(p)}
      style={{
        position: 'relative', overflow: 'hidden',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '2px 8px',
        cursor: onPriceSelect ? 'pointer' : 'default',
        backgroundColor: isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Depth bar */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0,
        [isBid ? 'right' : 'left']: 0,
        width: `${pct}%`,
        backgroundColor: isBid ? 'rgba(54,201,142,0.12)' : 'rgba(255,92,92,0.12)',
        pointerEvents: 'none',
      }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 5, overflow: 'hidden' }}>
        <span style={{ color: isBid ? '#36c98e' : '#ff5c5c', fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
          {p.toFixed(2)}
        </span>
        {orders != null && orders > 1 && (
          <span
            title={`${orders} resting orders at this price`}
            style={{ fontSize: 9, color: '#46525e', fontVariantNumeric: 'tabular-nums' }}
          >
            &times;{orders}
          </span>
        )}
      </div>
      <span style={{ position: 'relative', color: isHovered ? '#d7dde4' : '#7a8693', fontSize: 11, fontVariantNumeric: 'tabular-nums', transition: 'color 0.1s' }}>
        {s.toLocaleString()}
      </span>
    </div>
  );
});

const Empty = ({ text }: { text: string }) =>
  <div style={{ color: '#46525e', fontSize: 11, textAlign: 'center', padding: '10px 0' }}>{text}</div>;
